import { Link } from 'react-router';
import type { ScheduleRunSummaryDto } from '@verybigthings/trackframe-shared/schemas';
import { Badge } from '@/components/ui/badge';
import { TableCell, TableRow } from '@/components/ui/table';
import { formatScheduleDateTime, safeRunSummary, truncateId } from './schedule-ui-model';

/**
 * COP-134 D1 / ATRU-04 — the run-history table's columns, in render order.
 *
 * ONE source for the header cells, the body cells, and the empty-state
 * `colSpan`. A new column can no longer desynchronize the empty-state row,
 * because both read this array's length (`run-history-row.test.tsx` pins it).
 */
export const RUN_HISTORY_COLUMNS = [
  'Scheduled for',
  'Status',
  'Duration',
  'Attempt',
  'Next retry',
  'Session',
  'Approval id',
  'Trace id',
  'Error class',
  'Error message',
] as const;

export interface RunHistoryRowProps {
  run: ScheduleRunSummaryDto;
}

/**
 * One run row.
 *
 * Extracted from `RunHistory` so a unit test can render it without importing
 * `@/lib/hub-client` — that module constructs a `HubClient` at load time and
 * throws in the test environment (same reason `created-in-chat-badge.tsx`
 * lives on its own).
 *
 * The Session cell (COP-134 D1 / ATRU-04) is the point of the extraction: a
 * scheduled run writes a real chat session, and until now the run row was a
 * dead end for exactly the output someone is debugging. `—` when the turn
 * never opened a session (COP-131: absent, never `null`).
 *
 * M33 task-04b finishes the path: the cell is now a link to the read-only
 * session viewer at `/admin/sessions/show/:id`.
 */
export function RunHistoryRow({ run }: RunHistoryRowProps) {
  const summary = safeRunSummary(run);
  return (
    <TableRow>
      <TableCell>{summary.scheduledForLabel}</TableCell>
      <TableCell><Badge variant={summary.badgeVariant}>{summary.statusLabel}</Badge></TableCell>
      <TableCell>{summary.durationLabel}</TableCell>
      <TableCell>{summary.attemptCount}</TableCell>
      <TableCell>{formatScheduleDateTime(summary.nextRetryAt)}</TableCell>
      <TableCell>
        {/*
          COP-134 D1 / ATRU-04 — the reference becomes reachable. `—` renders as
          plain text, never an empty link: an absent id (COP-131: absent, never
          `null`) has no destination. The hub `session#read`-gates the target,
          so a caller who may not read the session gets the no-access state.
        */}
        {summary.sessionId ? (
          <Link
            to={`/admin/sessions/show/${summary.sessionId}`}
            className="font-mono text-xs underline underline-offset-2"
            data-testid="run-session-id"
            data-session-id={summary.sessionId}
            title={summary.sessionId}
          >
            {summary.sessionLabel}
          </Link>
        ) : (
          <span className="font-mono text-xs" data-testid="run-session-id">
            {summary.sessionLabel}
          </span>
        )}
      </TableCell>
      <TableCell>
        <span className="font-mono text-xs" title={summary.approvalId ?? undefined}>
          {summary.approvalId ?? '—'}
        </span>
      </TableCell>
      <TableCell>
        <span className="font-mono text-xs" title={summary.traceId ?? undefined}>
          {summary.traceId ? truncateId(summary.traceId, 12, 8) : '—'}
        </span>
      </TableCell>
      <TableCell>{summary.lastErrorClass ?? '—'}</TableCell>
      <TableCell>
        <span className="block max-w-80 truncate" title={summary.lastErrorMessage ?? undefined}>
          {summary.lastErrorMessage ?? '—'}
        </span>
      </TableCell>
    </TableRow>
  );
}
